import { Tag } from '@lastbrand/web-ui/tag'
import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'

import { FormattedPrice } from '@src/blocks/currency/currency.component'
import { ProductPriceDS, ProductPriceDSVariant } from '@src/components/blocks/product-price-ds'
import { PURCHASE_TYPES } from '@src/quince-components/src/atoms/purchaseTypeSelector/PurchaseTypeSelector'
import { fireThirdPartyProductEvents } from '@src/quince-site-common/src/helpers/productHelper'

import BestMarketValue from './best-market-value'
import FinalSaleChip from './final-sale-chip'
import PerUnitOfferRow from './per-unit-offer-row'
import { ProductInfoPriceDSProps, usePriceDisplayState } from './price-display-state'
import * as styles from './product-info-price-ds.styles'
import TraditionalPriceColumn from './traditional-price-column'

export type { ProductInfoPriceDSProps } from './price-display-state'

// Orchestrator: derives render flags via usePriceDisplayState, maps the scenario onto a
// ProductPriceDS variant, and keeps the leaves the Figma widget doesn't cover (the small
// traditional-retail row under offers, best-market-value, the b2b chip, per-unit offer row).
function ProductInfoPriceDS(props: ProductInfoPriceDSProps) {
  const {
    product,
    formattedSubscriptionPrice,
    formattedSalePrice,
    formattedTraditionalPrice,
    savingPercentage,
    formattedFestivalOfferPrice,
    pricePerServing,
    traditionalPricePerServing,
    festivalPricePerUnit,
    subscriptionOfferPricePerUnit,
    isFinalSaleProduct,
    isB2BFinalSaleProduct,
    purchaseOption,
    updatedDesignForV2,
    isVariantThirdParty,
    selectedOptions,
  } = props

  const { t } = useTranslation('common')
  const {
    isDiscountLabelPresent,
    hasPerUnitPricing,
    perUnitSavingsPercentage,
    showBestMarketValue,
    showSavingPercentage,
    showPerUnitSavingsPercentage,
    unitForPerServing,
    offerStyleKey,
    earnCreditTagFor3PVendor,
    isSubscriptionOffer,
    isSubscribePurchase,
  } = usePriceDisplayState(props)

  const isOffer = isSubscriptionOffer || !!formattedFestivalOfferPrice
  const showTraditionalColumn =
    !!formattedTraditionalPrice && isDiscountLabelPresent && !hasPerUnitPricing && !showBestMarketValue

  // Scenario → Figma ProductPrice variant. Offers strike the sale price beside the offer
  // value (festival wins the red styling when both offers are present, matching
  // getPriceStyleKey; the subscription price still wins the value slot, matching legacy).
  const variant: ProductPriceDSVariant = isOffer
    ? offerStyleKey === 'subscriptionOffer'
      ? 'singlePromoSub'
      : 'singlePromo'
    : hasPerUnitPricing && unitForPerServing
      ? 'servingSub'
      : showTraditionalColumn
        ? 'double'
        : 'single'

  // Savings text becomes the block caption; the two flags are mutually exclusive
  // (per-unit savings require hasPerUnitPricing, which suppresses the sale-price savings).
  const caption = showSavingPercentage
    ? t('you_save', { percentage: savingPercentage })
    : showPerUnitSavingsPercentage
      ? t('save_vs_traditional_retail', { percentage: perUnitSavingsPercentage })
      : undefined

  // Verbatim force-update selector classes preserved from the legacy markup.
  const salePriceNode = (
    <span className="sales_price_selector_for_force_update">
      <FormattedPrice amount={formattedSalePrice} />
    </span>
  )
  const offerPriceNode = isOffer ? (
    <span className="festival_sale_price_selector_for_force_update">
      <FormattedPrice
        amount={
          isSubscriptionOffer && formattedSubscriptionPrice
            ? formattedSubscriptionPrice
            : (formattedFestivalOfferPrice ?? '')
        }
      />
    </span>
  ) : undefined

  const finalSaleTagLabel = isFinalSaleProduct && !isB2BFinalSaleProduct ? t('final_sale') : undefined

  // Mount-only ([] deps) so the 3P events fire exactly once per mount — intentionally NOT
  // re-fired when the same mounted instance receives a new product (QATC / mini-PDP), matching legacy.
  useEffect(() => {
    if (isVariantThirdParty && product && selectedOptions) {
      fireThirdPartyProductEvents(product, selectedOptions, !!earnCreditTagFor3PVendor, showBestMarketValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <ProductPriceDS
        variant={variant}
        formattedSalePrice={salePriceNode}
        formattedOfferPrice={offerPriceNode}
        formattedTraditionalPrice={
          isOffer ? (
            salePriceNode
          ) : variant === 'double' ? (
            <span className="traditionalPrice_selector_for_force_update">
              <FormattedPrice amount={formattedTraditionalPrice} />
            </span>
          ) : undefined
        }
        caption={caption}
        secondCaption={variant === 'double' ? t('traditional_retail') : undefined}
        formattedPricePerServing={variant === 'servingSub' ? <FormattedPrice amount={pricePerServing} /> : undefined}
        formattedTraditionalPricePerServing={
          variant === 'servingSub' ? <FormattedPrice amount={traditionalPricePerServing} /> : undefined
        }
        unitForPerServing={unitForPerServing}
        tagLabel={earnCreditTagFor3PVendor}
        secondTagLabel={finalSaleTagLabel}
      />

      {/* The Figma double variant has no tag slots — keep the tags rendered below it. */}
      {variant === 'double' && (!!earnCreditTagFor3PVendor || !!finalSaleTagLabel) && (
        <div className={styles.tagsRowCss}>
          {earnCreditTagFor3PVendor && <Tag variant="cantaloupe">{earnCreditTagFor3PVendor}</Tag>}
          {finalSaleTagLabel && <Tag variant="offwhite">{finalSaleTagLabel}</Tag>}
        </div>
      )}

      {/* Under an offer the widget shows the promo pair; the traditional-retail row stays below. */}
      {isOffer && showTraditionalColumn && formattedTraditionalPrice && (
        <TraditionalPriceColumn
          formattedTraditionalPrice={formattedTraditionalPrice}
          formattedFestivalOfferPrice={formattedFestivalOfferPrice}
          isSubscriptionOffer={isSubscriptionOffer}
          updatedDesignForV2={updatedDesignForV2}
        />
      )}

      {showBestMarketValue && <BestMarketValue />}

      {isB2BFinalSaleProduct && !isFinalSaleProduct && (
        <div className={styles.priceWrapperMainMarginCss}>
          <FinalSaleChip variant="b2b" />
        </div>
      )}

      {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
      {(festivalPricePerUnit || (subscriptionOfferPricePerUnit && purchaseOption === PURCHASE_TYPES.SUBSCRIBE)) &&
        traditionalPricePerServing &&
        unitForPerServing && (
          <PerUnitOfferRow
            festivalPricePerUnit={festivalPricePerUnit}
            subscriptionOfferPricePerUnit={subscriptionOfferPricePerUnit}
            isSubscribePurchase={isSubscribePurchase}
            traditionalPricePerServing={traditionalPricePerServing}
            unitForPerServing={unitForPerServing}
          />
        )}
    </div>
  )
}

export default ProductInfoPriceDS
